import _ from "lodash";

import { WorkspacePermissionSubjectType, WorkspaceResourceType } from "common/interfaces";
import { getWorkspacePermissionsToResource } from "js/controllers/WorkspaceController";
import getUserProfile from "js/core/services/userProfiles";
import { AvatarStackProfile } from "js/editor/PresentationLibrary/components/AvatarStack";

export interface FetchCollaboratorProfilesParams {
    workspaceId: string;
    resourceId: string;
    ownerUid: string;
}

/**
 * Fetches collaborator profiles (owner + shared users) for a presentation.
 * Used by both list and grid views to show AvatarStack.
 */
export async function fetchPresentationCollaboratorProfiles({
    workspaceId,
    resourceId,
    ownerUid,
}: FetchCollaboratorProfilesParams): Promise<AvatarStackProfile[]> {
    let uids = [ownerUid];
    const permByUid: Record<string, { owner: boolean; write: { object: boolean } }> = {};

    try {
        const permissions = await getWorkspacePermissionsToResource({
            workspaceId,
            resourceId,
            resourceType: WorkspaceResourceType.PRESENTATION,
        });
        const users = permissions.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER);
        users.forEach(p => permByUid[p.subjectId] = p);
        uids = [ownerUid, ..._.uniq(users.map(p => p.subjectId).filter(uid => uid !== ownerUid))].slice(0, 4);
    } catch {
        // show owner only if permissions fetch fails
    }

    return Promise.all(
        uids.map(uid =>
            getUserProfile(uid).then(({ photoURL, displayName, email }) => ({
                photoURL,
                displayName,
                email,
                isReadOnly: !permByUid[uid]?.owner && !permByUid[uid]?.write?.object,
            }))
        )
    );
}
